import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ROLE_TYPE_CAREGIVER, ROLE_TYPE_FACILITY_WORKER, ROLE_TYPE_FAMILY_MEMBER, ROLE_TYPE_MHP, ROLE_TYPE_NURSE, ROLE_TYPE_PROVIDER } from "../../../constants/RegisterConstants";
import { useResponsive } from "../../../responsive/ResponsiveProvider";
import PublicRoleSelector from "../components/PublicRoleSelector";
import {
  publicMutedTextStyle,
  publicPageContainerStyle,
  publicSectionCardStyle,
} from "../../../components/general/publicPageStyles";
import { useAuthExperience } from "../../auth/hooks/useAuthExperience";

const getPlatformRoleContent = (providerBadge: string, websiteLabel: string): Record<string, any> => ({
  [ROLE_TYPE_PROVIDER]: {
    badge: providerBadge,
    title: `${providerBadge} Features`,
    theme: {
      accent: "#0f3d63",
      soft: "#eef6ff",
      border: "#cfe0f1",
      chip: "#e38b2c",
    },
    pricingBadge: "Pricing Available",
    hipaaBadge: "HIPAA-Compliant",
    featureSections: [
      {
        title: "Automation",
        items: [
          { title: "Care Plan Automation", description: "Generate faster draft care plans from resident assessments." },
          { title: "Behavior and Intervention Generation", description: "Generate behavior and intervention care plan document." },
          { title: "Preliminary Care Plan Generation", description: "Prepare an initial planning draft before full review." },
          { title: "Summarize Assessment", description: "Create quick summaries from long assessment documents." },
          { title: "Compare Assessments", description: "Highlight changes between two assessment versions." },
          { title: "Compare Care Plans", description: "Review what changed across care plan drafts." },
        ],
      },
      {
        title: "Operations",
        items: [
          { title: "Resident and Home Management", description: "Manage homes, residents, and profile details in one place." },
          { title: "Reminders and Care Coordination", description: "Track follow-ups, deadlines, and recurring tasks." },
          { title: "Binder Management", description: "Keep resident documents organized and easy to access." },
          { title: "Incident Reporting", description: "Document resident incidents in a structured workflow for provider follow-up." },
          { title: "Progress Notes", description: "Capture day-to-day resident notes in one organized provider workflow." },
          { title: "ADL Tracking", description: "Review scheduled activities of daily living, chart support levels and item-specific parameters, and generate daily or monthly reports." },
          { title: "Daily Behavior Tracker", description: "Track daily behaviors and generate monthly behavior reports from one place for CBHS/IBSS." },
        ],
      },
      {
        title: `${websiteLabel} Management`,
        items: [
          { title: "Website Editing", description: `Edit and publish the ${websiteLabel.toLowerCase()} from one workspace.` },
          { title: "Content Management", description: "Manage website content including text, images, and media." },
          { title: "Website Theme", description: `Choose from theme options to customize the look and feel of the ${websiteLabel.toLowerCase()}.` },
        ],
      },
      {
        title: "RN (Registered Nurse) Workflows",
        items: [
          { title: "Non-Medicaid Resident Assessments", description: "Create draft RN assessments for private-pay or non-Medicaid residents." },
          { title: "Non-Medicaid Delegations", description: "Manage delegation forms and related RN workflow for non-Medicaid residents." },
        ],
      },
    ],
  },
  [ROLE_TYPE_NURSE]: {
    badge: "Nurse",
    title: "Nurse Features",
    theme: {
      accent: "#0b5cab",
      soft: "#f4f9ff",
      border: "#d8e7f7",
      chip: "#0ea5e9",
    },
    pricingBadge: "Pricing Available",
    hipaaBadge: "HIPAA-Compliant",
    featureSections: [
      {
        title: "Assessment & Care Plan Automation",
        items: [
          { title: "Resident Assessment", description: "Build draft assessments from uploaded clinical documents, demographics,  medication list, MAR sheets, and other sources." },
          { title: "Body Chart", description: "System generated resident body chart for care plan." },
          { title: "Care Plan Automation", description: "Generate faster draft care plans from resident assessments." },
          { title: "Assessment Version Management", description: "Open previous saved assessment versions when needed." },
        ],
      },
      {
        title: "Delegation and Records",
        items: [
          { title: "Delegation Workflows", description: "Keep resident delegation work structured and easier to complete." },
          { title: "Delegation Form Management", description: "Save, review, and update delegation forms in one place." },
          { title: "Podiatrist - Foot and Nail Care", description: "Document podiatrist foot and nail care workflows alongside the resident record." },
          { title: "ADL Tracking", description: "Review and chart ADL support and tracking details for authorized residents." },
          { title: "Resident Document Management", description: "Store supporting assessment and delegation records together." },
          { title: `Resident Directory for Assigned ${providerBadge}s`, description: `See the residents connected to each assigned ${providerBadge.toLowerCase()}.` },
        ],
      },
    ],
  },
  [ROLE_TYPE_MHP]: {
    badge: "MHP",
    title: "Mental Health Professional Features",
    theme: {
      accent: "#0b5cab",
      soft: "#f4f9ff",
      border: "#d8e7f7",
      chip: "#0ea5e9",
    },
    pricingBadge: "Pricing Available",
    hipaaBadge: "HIPAA-Compliant",
    featureSections: [
      {
        title: "Assessment & Care Plan Automation",
        items: [
          { title: "Resident Assessment", description: "Build draft assessments from uploaded clinical documents, demographics, medication list, MAR sheets, and other sources." },
          { title: "Body Chart", description: "System generated resident body chart for care plan support." },
          { title: "Care Plan Automation", description: "Generate faster draft care plans from resident assessments." },
          { title: "Assessment Version Management", description: "Open previous saved assessment versions when needed." },
        ],
      },
      {
        title: "Mental Health Workflow Support",
        items: [
          { title: "Psychosocial Review Support", description: "Keep mental health and psychosocial workflow details easier to document and review." },
          { title: "Behavior and Intervention Drafting", description: "Generate and refine behavior-focused planning content from resident assessment details." },
          { title: "ADL Tracking", description: "Review and chart ADL support and tracking details for authorized residents." },
          { title: "Resident Document Management", description: "Store supporting assessment and care-planning records together." },
          { title: `Resident Directory for Assigned ${providerBadge}s`, description: `See the residents connected to each assigned ${providerBadge.toLowerCase()}.` },
        ],
      },
    ],
  },
  [ROLE_TYPE_CAREGIVER]: {
    badge: "Caregiver",
    title: "Caregiver Features",
    theme: {
      accent: "#166534",
      soft: "#f3fbf5",
      border: "#cfe8d6",
      chip: "#16a34a",
    },
    pricingBadge: "Pricing Available",
    hipaaBadge: "HIPAA-Compliant",
    featureSections: [
      {
        title: "Daily Workflow",
        items: [
          { title: "Reminders", description: "Keep important daily and future tasks visible." },
          { title: "Recurring Task Tracking", description: "Manage repeat work without rebuilding reminders each time." },
          { title: "Daily Coordination Tools", description: "Stay organized with simple day-to-day workflow support." },
          { title: "ADL Tracking", description: "Chart scheduled ADL actions, support levels, parameters, and notes for assigned residents." },
        ],
      },
      {
        title: "Shared Access",
        items: [
          { title: "Shared Document Access", description: "Open care-related files from a shared caregiver workspace." },
          { title: "Resident Information Review", description: "Check key resident details without extra system hopping." },
        ],
      },
    ],
  },
  [ROLE_TYPE_FACILITY_WORKER]: {
    badge: "Facility Worker",
    title: "Facility Worker Features",
    theme: { accent: "#166534", soft: "#f3fbf5", border: "#cfe8d6", chip: "#16a34a" },
    pricingBadge: "No subscription required",
    hipaaBadge: "Role-based access",
    featureSections: [
      {
        title: "Assigned Facility Tasks",
        items: [
          { title: "Assigned Task List", description: "View facility tasks assigned to you and keep track of the work that needs to be completed." },
          { title: "Task Actions", description: "Take action on assigned facility tasks and update their progress as work is completed." },
          { title: "Facility Operations", description: "Support facility-level work without resident or clinical access." },
          { title: "Free Role Access", description: "Facility Worker access does not require a subscription." },
        ],
      },
    ],
  },
  [ROLE_TYPE_FAMILY_MEMBER]: {
    badge: "Family Member",
    title: "Family Features",
    theme: {
      accent: "#7c2d12",
      soft: "#fff7ed",
      border: "#f1dbc7",
      chip: "#c76b11",
    },
    pricingBadge: "Pricing Available",
    hipaaBadge: "HIPAA-Compliant",
    featureSections: [
      {
        title: "Access",
        items: [
          { title: "Resident Document Access", description: "Review important resident files from one simple place." },
          { title: "Organized Information Review", description: "See information in a more structured, less scattered way." },
        ],
      },
      {
        title: "Experience",
        items: [
          { title: "Simple Ongoing Access", description: "Stay connected to the information you need over time." },
          { title: "Low-Friction Family Workflow", description: "Use a simpler family-friendly experience for review and follow-up." },
        ],
      },
    ],
  },
});

const resolvePlatformFeaturesRole = (roleParam: string | null, availableRoles: string[] = []) => {
  const normalizedRole = String(roleParam || "").trim().toLowerCase();
  const supportedRoles = availableRoles.length > 0 ? availableRoles : [ROLE_TYPE_PROVIDER, ROLE_TYPE_NURSE, ROLE_TYPE_CAREGIVER];
  return supportedRoles.includes(normalizedRole) ? normalizedRole : ROLE_TYPE_PROVIDER;
};

const PlatformFeatures = () => {
  const authExperience = useAuthExperience();
  const availableRoleTypes = useMemo(() => authExperience.roleOptions.map((role) => role.value), [authExperience.roleOptions]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedRole, setSelectedRole] = useState<string | null>(() => resolvePlatformFeaturesRole(searchParams.get("role"), availableRoleTypes));
  const mobile = useResponsive().isMobile;
  const platformRoleContent = useMemo(
    () => getPlatformRoleContent(authExperience.nurseWorkspace.providerLabel, authExperience.discovery.jobWebsiteLabel),
    [authExperience.discovery.jobWebsiteLabel, authExperience.nurseWorkspace.providerLabel],
  );
  const roleContent = selectedRole ? platformRoleContent[selectedRole] : null;
  const featureCount = roleContent
    ? roleContent.featureSections.reduce((count: number, section: any) => count + section.items.length, 0)
    : 0;

  useEffect(() => {
    const resolvedRole = resolvePlatformFeaturesRole(searchParams.get("role"), availableRoleTypes);
    setSelectedRole((currentRole) => (currentRole === resolvedRole ? currentRole : resolvedRole));
  }, [availableRoleTypes, searchParams]);

  useEffect(() => {
    const resolvedRole = selectedRole || ROLE_TYPE_PROVIDER;
    const currentRoleParam = String(searchParams.get("role") || "").trim().toLowerCase();
    if (currentRoleParam === resolvedRole) {
      return;
    }

    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set("role", resolvedRole);
    setSearchParams(nextSearchParams, { replace: true });
  }, [searchParams, selectedRole, setSearchParams]);

  return (
    <div style={publicPageContainerStyle}>
      <div>
        <PublicRoleSelector
          selectedRole={selectedRole}
          onChange={setSelectedRole}
          title="Choose A Role To Explore Features"
          compact
          inline
        />
      </div>

      {!roleContent ? (
        <div className="mt-3" style={publicSectionCardStyle}>
          <div style={{ color: "#0f3d63", fontSize: "1.2rem", fontWeight: 700 }}>Select a role to view the feature grid</div>
          <p style={{ ...publicMutedTextStyle, margin: "0.75rem 0 0" }}>
            Platform Features is organized by role so you can see only the relevant feature list.
          </p>
        </div>
      ) : (
        <div className="mt-3" style={publicSectionCardStyle}>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "0.75rem",
              alignItems: "center",
              marginBottom: "1rem",
            }}
          >
            <div style={{ minWidth: 0, flex: 1 }}>
              <div
                className="inline-flex align-items-center gap-2 px-3 py-2 border-round-2xl mb-2"
                style={{
                  backgroundColor: roleContent.theme.soft,
                  border: `1px solid ${roleContent.theme.border}`,
                  color: roleContent.theme.accent,
                  fontWeight: 700,
                }}
              >
                <i className="pi pi-th-large" style={{ color: roleContent.theme.chip }} />
                {roleContent.badge}
              </div>
              <h2 style={{ color: roleContent.theme.accent, margin: 0, fontSize: "1.8rem" }}>{roleContent.title}</h2>
              <div style={{ ...publicMutedTextStyle, marginTop: "0.35rem" }}>
                {featureCount} features for this role.
              </div>
            </div>
          </div>

          <div
            style={{
              border: `1px solid ${roleContent.theme.border}`,
              borderRadius: "22px",
              overflow: "hidden",
              background: "#ffffff",
              boxShadow: "0 14px 32px rgba(15, 61, 99, 0.06)",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: mobile ? "1fr" : "minmax(170px, 220px) 1fr",
                borderBottom: `1px solid ${roleContent.theme.border}`,
                background: roleContent.theme.soft,
              }}
            >
              <div style={{ padding: "1rem 1.1rem", fontWeight: 800, color: roleContent.theme.accent, letterSpacing: "0.04em", textTransform: "uppercase", fontSize: "0.8rem" }}>
                Role
              </div>
              <div style={{ padding: "1rem 1.1rem", fontWeight: 800, color: roleContent.theme.accent, letterSpacing: "0.04em", textTransform: "uppercase", fontSize: "0.8rem" }}>
                Feature List
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: mobile ? "1fr" : "minmax(170px, 220px) 1fr",
              }}
            >
              <div
                style={{
                  padding: "1.15rem 1.1rem",
                  borderRight: mobile ? "none" : `1px solid ${roleContent.theme.border}`,
                  borderBottom: mobile ? `1px solid ${roleContent.theme.border}` : "none",
                  background: roleContent.theme.soft,
                }}
              >
                <div style={{ color: roleContent.theme.accent, fontWeight: 800, fontSize: "1rem" }}>{roleContent.badge}</div>
                <div style={{ ...publicMutedTextStyle, marginTop: "0.35rem", fontSize: "0.9rem" }}>
                  {featureCount} features
                </div>
              </div>

              <div style={{ padding: "1.15rem 1.1rem" }}>
                <div className="grid">
                  {roleContent.featureSections.map((section: any) => (
                    <div key={section.title} className="col-12 md:col-6">
                      <div
                        style={{
                          border: `1px solid ${roleContent.theme.border}`,
                          borderRadius: "18px",
                          background: "#fcfdff",
                          padding: "0.85rem 0.95rem",
                          height: "100%",
                        }}
                      >
                        <div
                          style={{
                            color: roleContent.theme.accent,
                            fontWeight: 800,
                            fontSize: "0.95rem",
                            marginBottom: "0.55rem",
                          }}
                        >
                          {section.title}
                        </div>
                        <ul style={{ margin: 0, paddingLeft: "1.1rem", color: "#334155", lineHeight: 1.8 }}>
                          {section.items.map((feature: any) => (
                            <li key={feature.title} style={{ marginBottom: "0.45rem" }}>
                              <div style={{ fontWeight: 700, color: "#0f172a" }}>{feature.title}</div>
                              <div style={{ color: "#64748b", fontSize: "0.9rem", lineHeight: 1.55 }}>
                                {feature.description}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlatformFeatures;
